import React, { useRef, useEffect, useState, useContext, forwardRef, useImperativeHandle } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

// Componentes y Peticiones de API
import AgGrid from '../../../../Base/Components/Tables/AgGrid/AgGrid';
import { authByUserRol } from '../../../../API/Mantenimiento/Requisas/RequisasMantenimientoRequests';
import { AuthContext } from '../../../../Base/Context/AuthContext/AuthContext';

/**
 * COMPONENTE: EncRequisaOverlayPanel
 * PROPÓSITO: Panel flotante para seleccionar encabezados de requisas filtradas por permisos de usuario.
 * NOTA: Utiliza 'forwardRef' para permitir que componentes padres controlen su apertura/cierre.
 */
export const EncRequisaOverlayPanel = forwardRef(({ close, rowData, onChange }, ref) => {

    // --- CONTEXTO Y REFERENCIAS ---
    // Extrae los datos de autenticación del usuario logueado
    const { userAuth } = useContext(AuthContext);
    const op = useRef(null); // Referencia interna al componente OverlayPanel de PrimeReact

    // --- ESTADOS (STATE) ---
    const [loading, setLoading] = useState(false);  // Estado para el spinner de carga
    const [encabezado, setEncabezado] = useState([]); // Lista de requisas para la tabla

    /**
     * FUNCIÓN ASÍNCRONA: obtenerProductos
     * Consulta el backend enviando IdUser e IdRol para aplicar filtros de seguridad
     * desde el Procedimiento Almacenado.
     */
    const obtenerEncabezado = async () => {
        setLoading(true);
        // Preparamos los parámetros del usuario para el filtro del SP
        const temporal = {
            IdUser: userAuth.idUser,
            IdRol: userAuth.IdRol,
        };
        try {
            // ÚNICA FUENTE DE DATOS: Petición POST con filtros de rol
            const tempoAuth = await authByUserRol(temporal);
            // Ordenamos la respuesta descendente por IdRequisa (la más nueva arriba)
            const ordenado = (tempoAuth || []).sort(
                (a, b) => b.IdRequisa - a.IdRequisa
            );
            setEncabezado(ordenado);
        } catch (error) {
            
            setEncabezado([]); // Limpiamos en caso de fallo
        } finally {
            setLoading(false);
        }
    };

    /**
    * HOOK: useImperativeHandle
    * Expone funciones específicas hacia el componente padre mediante la referencia 'ref'.
    * Esto permite ejecutar 'ref.current.toggle(e)' desde fuera de este archivo.
    */
    useImperativeHandle(ref, () => ({
        toggle: (event) => {
            // Cada vez que se intenta abrir el panel, refrescamos los datos automáticamente
            obtenerEncabezado();
            op.current.toggle(event);
        }
    }));

    /**
     * DEFINICIÓN DE COLUMNAS PARA AG-GRID
     * Se configura la visibilidad basada en el rol del usuario.
     */
    const columns = [
        { field: 'IdRequisa', header: 'IdRequisa', className: 'Xsmall', hidden: userAuth.IdRol === 1},
        { field: 'Requisa', header: 'No. Requisa', className: 'small'},
        { field: 'Area', header: 'Area', className: 'large' },
        { field: 'TecnicoRecibe', header: 'Tecnico', className: 'large' },
        { field: 'Solicita', header: 'Agente', className: 'large' },
        { field: 'UserName', header: 'UsuarioCreo', classNames: 'large' }


    ];

    return (
        /* OverlayPanel: Contenedor flotante que aparece cerca del botón de búsqueda */
        <OverlayPanel
            ref={op}
            showCloseIcon
            dismissable={false}
            className='OverPanelProductos'
            onHide={close}
        >
            {/* AgGrid: Tabla optimizada para mostrar los datos obtenidos por rol */}
            <div style={{ width: 600, height: 400 }}>
                {/* Aseguramos que el objeto table se cree con los datos actuales */}
                <AgGrid table={{
                    Columns: columns,
                    Data: encabezado, // Este es el estado que se actualiza
                    loading: loading,
                    onCellClicked: (e) => {
                        // Al seleccionar una celda, enviamos los datos al componente padre (onChange)
                        onChange(e, rowData);
                        // Cerramos el panel automáticamente tras la selección
                        op.current.hide();
                    }
                }} />
            </div>
        </OverlayPanel>
    );
});